/**
 * THE MARKING ENGINE, ON THE DEVICE — the same code the server runs, not a second implementation.
 *
 * Founder, 2026-08-12: *"was wondering if slm can be included in digital university, then why not
 * full marking engine — idea was if slm is installable, secondly if slm is optional, 3rd course
 * corpus is absolutely a MUST."*
 *
 * He is right, and my earlier objection was weak. I kept the marking engine online largely because
 * its 202 specs are 0.54 MB gzipped, "bigger than everything else we ship offline combined". That is
 * true and it is beside the point: against a Qwen2.5-7B at ~4.6 GB it is **0.012%**. Arguing 0.54 MB
 * was too heavy while planning to ship 4.6 GB was bad reasoning about scale.
 *
 * ── THE THREE POINTS ARE A LAYERING, AND THE ORDER MATTERS ─────────────────────────────────────
 *   3. THE COURSE CORPUS IS A MUST      the floor. Without it there is no Academy, only an app.
 *   2. THE SLM IS OPTIONAL              so nothing below it may DEPEND on it.
 *   1. THE SLM IS INSTALLABLE           so size stopped being the reason to leave things ashore.
 *
 * This engine sits between 3 and 2: heavier than the corpus, weightless beside the model, and — the
 * part that matters — **it calls no model at all**. It is deterministic arithmetic over authored
 * specs, so it still works when the SLM is absent. That is FP-010 exactly: the reliable floor does
 * not depend on the intelligence layered above it.
 *
 * ── WHY THE SPECS ARE FETCHED, NOT BUNDLED ─────────────────────────────────────────────────────
 * Inlining 1.8 MB of specs into the JS bundle would make the app itself heavier for every candidate,
 * including one who never opens a drawing. They are fetched once and cached by the service worker
 * like everything else in "prepare for sea" — so the ENGINE (14 KB) rides in the bundle and its DATA
 * arrives with the rest of the offline set.
 */
import { useSpecs, check as engineCheck, loadSpecs } from '../../../drawing-check.mjs';

let _ready = false;

/** Load the specs into the engine. Idempotent; returns how many it now holds. */
export async function armMarking() {
  if (_ready) return loadSpecs().length;
  const r = await fetch('/api/drawing/specs');
  if (!r.ok) throw new Error(`the drawing specs are unavailable (HTTP ${r.status})`);
  const body = await r.json();
  const specs = body.specs || body;
  if (!Array.isArray(specs) || !specs.length) throw new Error('the drawing specs arrived empty');
  useSpecs(specs);
  _ready = true;
  return specs.length;
}

/**
 * Mark a submission HERE. Identical arithmetic to the server's, because it is the same function —
 * a second implementation would drift silently and neither copy would say which to believe.
 */
export function markLocally(drawing, submission) {
  if (!_ready) return { kind: 'null', reason: 'the marking engine has no specs yet — arm it first' };
  const r = engineCheck(drawing, submission);
  /* Say WHERE it was marked. A mark computed on the device is not a different mark, but a candidate
     is entitled to know that nothing left his phone (FP-018: provenance is part of the fact). */
  return r && r.kind !== 'null' ? { ...r, marked_on: 'this device' } : r;
}

export const markingReady = () => _ready;

/* The cross-engine gate reaches for these, exactly as it does for the almanac. A gate that tested a
   special build would be testing something nobody ships. */
globalThis.__ankrMarking = { armMarking, markLocally, markingReady };
